import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, FileSpreadsheet, Loader2, RefreshCw } from "lucide-react";
import { useRef } from "react";

interface Props {
  fileName: string;
  sourcePlatform: string;
  formatLabel?: string;
  rowCount?: number | null;
  columnCount?: number | null;
  isReplacing: boolean;
  onReplace: (file: File) => void;
}

/**
 * Variant A's file header card — shows once a CSV has been parsed for the job.
 * 44×44 amber tile + filename mono + green Parsed pill, metadata row to the
 * right, Replace button on the far right (re-uses the page's existing upload
 * mutation; we just open a hidden file input here).
 *
 * v1 deliberately omits the design's Download button: we don't store original
 * CSV bytes, only parsed rawRows. Reconstruction would be lossy. Add later
 * once we persist the original file alongside the parsed data.
 */
export function FileHeaderCard({
  fileName,
  sourcePlatform,
  formatLabel,
  rowCount,
  columnCount,
  isReplacing,
  onReplace,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex items-center gap-4 rounded-md border bg-card p-4">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300">
        <FileSpreadsheet className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-medium">{fileName}</span>
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Parsed
          </Badge>
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span>Source: {sourcePlatform}</span>
          {formatLabel && <span>· {formatLabel}</span>}
          {columnCount != null && (
            <span>· {columnCount.toLocaleString()} columns</span>
          )}
          {rowCount != null && (
            <span>· {rowCount.toLocaleString()} rows</span>
          )}
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onReplace(f);
          e.target.value = "";
        }}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={isReplacing}
        className="shrink-0"
      >
        {isReplacing ? (
          <>
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            Parsing…
          </>
        ) : (
          <>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Replace file
          </>
        )}
      </Button>
    </div>
  );
}
